import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  type CmsAppShortcutAction,
  type CmsShortcut,
  type CmsAppShortcut,
  type CmsGameShortcut,
  type CmsShortcutItem,
  getGameDisplayName,
  useGamesStore,
} from '@nsftx/seven-client-retail-vue-sgp'
import { isAppShortcut, isGameShortcut } from './shortcutsService'
import {
  SHORTCUT_CATEGORIES,
  createShortcutCategoryRecord,
  mapToShortcutCategoryItems,
} from './shortcutsUtils'

export const useShortcutsStore = defineStore('shortcutsStore', () => {
  const appShortcuts = ref<CmsAppShortcut[]>([])
  const gameShortcuts = ref<CmsGameShortcut[]>([])
  const { getGameLinkByCode } = useGamesStore()

  const shortcutCategories = computed(() => {
    const categories = createShortcutCategoryRecord()

    SHORTCUT_CATEGORIES.forEach((category) => {
      if (category === 'products') {
        const shortcutsByGameDisplayId = gameShortcuts.value
          .reduce<Record<string, CmsGameShortcut[]>>((item, shortcut) => {
            const { displayId } = shortcut.game
            item[displayId] ??= []
            item[displayId].push(shortcut)

            return item
        }, {})

        categories.products = Object.keys(shortcutsByGameDisplayId)
          .sort()
          .map((displayId) => ({
            label: getGameDisplayName(getGameLinkByCode(displayId)!),
            shortcuts: shortcutsByGameDisplayId[displayId] ? mapToShortcutCategoryItems(shortcutsByGameDisplayId[displayId]): [],
          }))

        return
      }

      const appCategoryShortcuts =  appShortcuts.value.filter((shortcut) => shortcut.category === category)
      categories[category] = mapToShortcutCategoryItems(appCategoryShortcuts)
    })

    return categories
  })

  const setShortcuts = (shortcuts: CmsShortcut[]) => {
    appShortcuts.value = shortcuts.filter(isAppShortcut)
    gameShortcuts.value = shortcuts.filter(isGameShortcut)
  }

  const getGameShortcutsByDisplayId = (displayId: string) => (
    gameShortcuts.value.filter((shortcut) => shortcut.game.displayId === displayId && shortcut.isActive)
  )

  const getAppShortcutsByAction = (action: CmsAppShortcutAction) => (
    appShortcuts.value.find((shortcut) => shortcut.action === action && shortcut.isActive)
  )

  const getNavigateGameShortcutById = (gameId: string) => (
    appShortcuts.value.find((shortcut) => shortcut.action === 'OPEN_GAME' && shortcut.isActive && shortcut.subject?.value === gameId)
  )

  const getShortcutItemKey = (shortcutItem: CmsShortcutItem) => {
    if (!shortcutItem.modifier?.length) {
      return shortcutItem.key?.toLowerCase()
    }

    return `${shortcutItem.modifier.join('+')}${shortcutItem.key ? `+${shortcutItem.key.toLowerCase()}` : ''}`
  }

  const getShortcutsByKey = (key: string) => ([...appShortcuts.value, ...gameShortcuts.value]
    .filter(({ shortcut }) => (
      shortcut.some((shortcutItem) => getShortcutItemKey(shortcutItem) === key)
    ))
  )

  return {
    appShortcuts,
    gameShortcuts,
    shortcutCategories,
    setShortcuts,
    getAppShortcutsByAction,
    getShortcutsByKey,
    getGameShortcutsByDisplayId,
    getNavigateGameShortcutById
  }
})
